<script>
import SignalButton from '@/V2/documents/document-view/SignalButton';

import {
  iTooltipAlignmentMode,
  iAccordion,
  iAccordionItem,
  iAccordionItemSizes,
  iBadge,
  iBadgeColours,
  iIcon,
  iIconButton,
  iMenu,
  iMenuItem,
  iMenuTriggerAlignOptions,
  iTooltip,
} from '@inscribe/signal';
import DocumentRightPanelEmptyState from '@/V2/documents/document-view/DocumentRightPanelEmptyState';
import palette from '@inscribe/signal/dist/palette';

export default {
  components: {
    DocumentRightPanelEmptyState,
    iAccordion,
    iAccordionItem,
    iBadge,
    iIcon,
    iIconButton,
    iMenu,
    iMenuItem,
    iTooltip,
    SignalButton,
  },
  props: {
    document: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      iBadgeColours,
      iAccordionItemSizes,
      iMenuTriggerAlignOptions,
      iTooltipAlignmentMode,
      palette,
      externalLink:
        'https://help.inscribe.ai/en/articles/8976050-trust-signals',
      signalsConfig: {
        image_metadata_as_expected: {
          title: 'Image Metadata As Expected',
          subtitle: 'Metadata matches the expected format',
          order: 4,
        },
        transactions_balance: {
          title: 'Transactions Balance',
          subtitle: 'Bank statement is balanced',
          order: 1,
        },
        image_pdf_expected: {
          title: 'Image PDF Expected',
          subtitle: 'Document format matches bank standard',
          order: 2,
        },
        location_match: {
          title: 'Location Match',
          subtitle: 'Photo location matches document address',
          order: 5,
        },
        photo_metadata_as_expected: {
          title: 'Camera Metadata As Expected',
          subtitle: 'Metadata corresponds with the capture device',
          order: 3,
        },
      },
    };
  },
  computed: {
    trustSignals() {
      const trust_signals = [];
      for (const signalKey of Object.keys(this.document.trust_signals || {})) {
        const signalConfig = this.signalsConfig[signalKey];
        if (signalConfig) {
          trust_signals.push({
            key: `${signalKey}`,
            title: signalConfig.title,
            subtitle: signalConfig.subtitle,
            order: signalConfig.order,
          });
        }
      }

      // sort
      trust_signals.sort((t1, t2) => {
        return t1.order > t2.order ? 1 : -1;
      });

      return trust_signals;
    },
  },
  methods: {
    openDecisionEngine() {
      this.$router.push({ name: 'decision-engine-detectors' });
    },
    stopPropagation(evt) {
      evt.stopPropagation();
    },
  },
};
</script>

<template>
  <i-accordion data-testid="trust-signals-list">
    <i-accordion-item
      title="Trust Signals"
      class="half-margin"
      :no-background="true"
      :mount-open="true"
      :size="iAccordionItemSizes.LARGE"
    >
      <div
        slot="extra-title-content"
        class="d-flex flex-row align-items-center justify-content-start i-gap-4"
      >
        <!-- info icon -->
        <i-icon
          symbol="small-question-mark"
          :size="12"
          :colour="palette['light-neutral-80']"
          class="trust-signals-list-help-icon"
        ></i-icon>
        <i-tooltip
          :target="`.trust-signals-list-help-icon`"
          title="Trust Signals"
          description="Document trust signals refer to common features of documents that indicate it is less likely to be fraudulent."
          link-text="Learn more"
          :link-external-url="externalLink"
          :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
        />

        <!-- summary badge -->
        <i-badge
          :label="trustSignals.length + ' Detected'"
          :colour="iBadgeColours.NEUTRAL"
          :outline="true"
        ></i-badge>
      </div>
      <div class="document-signals-list d-flex flex-column accordion-content">
        <!-- empty state -->
        <document-right-panel-empty-state
          v-if="!trustSignals || trustSignals.length === 0"
          data-testid="trust-signals-list-empty-state"
          symbol="medium-file-search"
          title="No trust signals detected"
          sub-title="Trust signals display here when detected"
          button-text="Manage Detectors"
          class="i-mx-6"
          @on-button-click="openDecisionEngine"
        ></document-right-panel-empty-state>

        <div v-else class="checks-list d-flex flex-column i-gap-1">
          <signal-button
            v-for="(signal, signal_index) in trustSignals"
            :key="signal_index"
            :title="signal.title"
            :subtitle="signal.subtitle"
            :success="true"
          >
            <!-- signal tools-->
            <div
              slot="tools"
              class="signal-tools d-flex flex-row align-items-center i-pl-8 i-pr-6 i-py-2 i-gap-6"
              @click="stopPropagation"
            >
              <i-menu
                :trigger-align="iMenuTriggerAlignOptions.RIGHT"
                :width="250"
              >
                <i-icon-button
                  slot="trigger"
                  class="trigger-button"
                  data-testid="signal-actions-button"
                  size="md"
                  symbol="medium-more-menu-vertical"
                />

                <!-- manage Signals -->
                <i-menu-item
                  slot="items"
                  label="Manage Detectors"
                  icon="small-setting-cog"
                  @item-click="openDecisionEngine()"
                ></i-menu-item>

                <!-- Learn More  -->
                <i-menu-item
                  slot="items"
                  label="Learn More"
                  icon="small-info-circle"
                  right-icon="small-arrows-expand"
                  :right-icon-color="palette['light-neutral-70']"
                  :link-external-url="externalLink"
                ></i-menu-item>
              </i-menu>
            </div>
          </signal-button>
        </div>
      </div>
    </i-accordion-item>
  </i-accordion>
</template>
